import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

export interface ClaimsPanopticonRun {
  id: string;
  workflow_id: string;
  workflow_run_id: string | null;
  run_scope: string;
  source_window_started_at: string | null;
  source_window_ended_at: string | null;
  total_matters: number;
  total_coverages: number;
  analyzed_coverages: number;
  failed_coverages: number;
  summarized_matters: number;
  failed_matters: number;
  needs_attention_count: number;
  status: string;
  slack_status: string;
  slack_message_ts: string | null;
  input_payload: Record<string, unknown>;
  result_payload: Record<string, unknown>;
  error: string | null;
  created_at: string;
  modified_at: string;
}

export interface ClaimsPanopticonCoverageAnalysis {
  id: string;
  run_id: string;
  matter_id: string;
  coverage_id: string;
  severity: string;
  needs_attention: boolean;
  current_stage: string;
  primary_owner: string;
  analysis_summary: string;
  source_ids: Record<string, unknown>;
  extracted_context: Record<string, unknown>;
  classification: Record<string, unknown> | null;
  llm_error: string | null;
  created_at: string;
  modified_at: string;
}

export interface ClaimsPanopticonMatterSummary {
  id: string;
  run_id: string;
  matter_id: string;
  severity: string;
  needs_attention: boolean;
  current_stage: string;
  slack_summary: string;
  summary: Record<string, unknown> | null;
  llm_error: string | null;
  created_at: string;
  modified_at: string;
}

export interface ClaimsPanopticonRunListResponse {
  runs: ClaimsPanopticonRun[];
}

export interface ClaimsPanopticonRunDetailResponse {
  run: ClaimsPanopticonRun;
  coverage_analyses: ClaimsPanopticonCoverageAnalysis[];
  matter_summaries: ClaimsPanopticonMatterSummary[];
}

export interface DeleteClaimsPanopticonRunResponse {
  deleted_run_id: string;
  workflow_id: string;
  deleted_coverages: number;
  deleted_matters: number;
}

export interface ClearClaimsPanopticonResponse {
  deleted_runs: number;
  deleted_coverages: number;
  deleted_matters: number;
}

export const claimsPanopticonApiClient = createApi({
  reducerPath: 'claimsPanopticonApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['ClaimsPanopticon'],
  endpoints: (builder) => ({
    getClaimsPanopticonRuns: builder.query<ClaimsPanopticonRunListResponse, number | void>({
      query: (limit = 50) => ({
        url: `api/admin/claims-panopticon/runs/?limit=${limit}`,
        method: 'GET',
      }),
      providesTags: (result) => [
        { type: 'ClaimsPanopticon', id: 'LIST' },
        ...(result?.runs ?? []).map((run) => ({
          type: 'ClaimsPanopticon' as const,
          id: run.id,
        })),
      ],
    }),
    getClaimsPanopticonRunDetail: builder.query<
      ClaimsPanopticonRunDetailResponse,
      string
    >({
      query: (runId) => ({
        url: `api/admin/claims-panopticon/runs/${runId}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, runId) => [
        { type: 'ClaimsPanopticon', id: runId },
      ],
    }),
    deleteClaimsPanopticonRun: builder.mutation<
      DeleteClaimsPanopticonRunResponse,
      string
    >({
      query: (runId) => ({
        url: `api/admin/claims-panopticon/runs/${runId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, runId) => [
        { type: 'ClaimsPanopticon', id: 'LIST' },
        { type: 'ClaimsPanopticon', id: runId },
      ],
    }),
    clearClaimsPanopticon: builder.mutation<ClearClaimsPanopticonResponse, void>({
      query: () => ({
        url: 'api/admin/claims-panopticon/clear/',
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'ClaimsPanopticon', id: 'LIST' }],
    }),
  }),
});

export const {
  useClearClaimsPanopticonMutation,
  useDeleteClaimsPanopticonRunMutation,
  useGetClaimsPanopticonRunDetailQuery,
  useGetClaimsPanopticonRunsQuery,
} = claimsPanopticonApiClient;
